// Semantic (position-dependent) validation for RTableGroup payloads, layered
// on top of the format checks in validate.ts.
//
//   create       - format + the pinned RSchema resolves (a MISSING schema
//                  object or binding target is an infrastructure error:
//                  throw, never `false`) + every initial row validates as an
//                  insert against the pinned schema.
//   row          - format + table exists in the effective schema at `at` +
//                  the op conforms to it + identity rules: rowIds are
//                  write-once (an insert is valid only if NO op for its
//                  rowId — insert or delete — is at or below `at`, so a
//                  deleted rowId can never be re-inserted); updates and
//                  deletes need a live row + the row restriction passes at the
//                  parent frontier `(at, at)`.
//   ref-advance  - EITHER the schema deploy (refId is the group's schema ref:
//                  monotonic against the schema DAG, at or above the pinned
//                  version; when authored, the signature is verified at
//                  validation and canDeploy is evaluated in 'object' context
//                  against the verified author; PLUS the one-time add-fk
//                  prerequisite — a deploy whose newly-added/retargeted FK
//                  would strand an existing live row at `at` is hard-rejected,
//                  since FK reach is at-use and would otherwise leave the old
//                  row live-but-dangling) OR a foreign-group observation (refId
//                  is a bound group: monotonic against that group's DAG, no
//                  deploy gate). Any other refId is unknown (false).
//   bundle       - format + every table exists + per-op schema conformance +
//                  per-rowId uniqueness across the bundle + identity/liveness
//                  and restrictions at the PRE-state (`at`) + FK checks at each
//                  op's sequential cut (`at` plus the inserts before it in
//                  bundle order, minus the deletes before it).
//
// FK write-time checks (insert/update ops carrying FK columns): the named
// target must be live. A local target resolves on a sibling table at `at`
// (sequential cut inside a bundle); a `group.table` target resolves through
// the bound foreign group at the foreign version observed at `at` (an absent
// foreign table is a missing reference, so the dangling write is rejected).
// At-use op-voiding (view-time, in computeEntryVoided) handles cases the
// parent-frontier validation check cannot catch: a barrier delete of a
// restriction witness or FK target CONCURRENT with the write voids it at the
// merge (a causally-later delete is inert).
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { json } from "@hyper-hyper-space/hhs3_json";
import { version, validationFailure, validationOk, wrapValidationFailure, } from "@hyper-hyper-space/hhs3_mvt";
import { isRefAdvancePayload, extractRefVersion, validateRefAdvanceMonotonicity, refVersionAtOrAbove, extractAuthor, verifyPayloadSignature, } from "@hyper-hyper-space/hhs3_mvt";
import { splitTableRef } from "../rschema/payload.js";
import { formatPredicate, formatRestrictionFailureReason } from "../rschema/format_predicate.js";
import { collectExistsAtoms } from "../rschema/validate.js";
import { isValidTableRef } from "../rschema/validate.js";
import { validateInsertAgainstSchema, validateRowOpAgainstSchema, validateProviderInsertIntegrity } from "../rtable/validate_ops.js";
import { validateTableGroupPayloadFormat } from "./validate.js";
import { evaluatePredicate, evaluateRowOpRestriction } from "./predicates.js";
export function validateTableGroupPayload(payload, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const formatResult = validateTableGroupPayloadFormat(payload);
        if (!formatResult.valid)
            return formatResult;
        if (context.mode === 'create') {
            return validateCreate(payload, context.ctx);
        }
        const { group, at } = context;
        if (isRefAdvancePayload(payload)) {
            return validateRefAdvance(payload, group, at);
        }
        const action = payload['action'];
        if (action === 'row') {
            return wrapValidationFailure('row envelope rejected', yield validateRowEnvelope(payload, group, at), group.getId());
        }
        if (action === 'bundle') {
            return wrapValidationFailure(`bundle rejected by group '${group.getId()}'`, yield validateBundle(payload, group, at), group.getId());
        }
        // 'create' is never applied to an existing group
        return validationFailure("create payload cannot be applied to an existing table group", { objectHash: group.getId() });
    });
}
function validateCreate(create, ctx) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        // a missing referenced object is an infrastructure error, never an MVT
        // data condition: throw
        const schemaObj = yield ctx.getObject(create.schemaRef);
        if (schemaObj === undefined) {
            throw new Error(`RSchema '${create.schemaRef}' is not present in the replica`);
        }
        const schema = schemaObj;
        const pinned = version(...json.fromSet(create.schemaVersion));
        const schemaView = yield schema.getView(pinned, pinned);
        const hashSuite = ctx.getHashSuite();
        for (const table of Object.keys((_a = create.initialRows) !== null && _a !== void 0 ? _a : {})) {
            if (!schemaView.hasTable(table))
                return validationFailure(`initial row table '${table}' does not exist in pinned schema`);
            const seenRowIds = new Set();
            for (const [index, row] of create.initialRows[table].entries()) {
                const insert = row;
                if (seenRowIds.has(insert.rowId))
                    return validationFailure(`duplicate initial rowId '${insert.rowId}' in table '${table}'`);
                seenRowIds.add(insert.rowId);
                const schemaResult = validateInsertAgainstSchema(insert, schemaView, table);
                if (!schemaResult.valid) {
                    return wrapValidationFailure(`initial row ${index} in table '${table}' does not match schema`, schemaResult);
                }
                // genesis rows of a provider table must be self-certifying too
                const providerResult = validateProviderInsertIntegrity(insert, schemaView, table, hashSuite);
                if (!providerResult.valid) {
                    return wrapValidationFailure(`initial provider row ${index} in table '${table}' is not self-certifying`, providerResult);
                }
            }
        }
        for (const [name, target] of Object.entries((_b = create.bindings) !== null && _b !== void 0 ? _b : {})) {
            const bound = yield ctx.getObject(target);
            if (bound === undefined) {
                throw new Error(`Binding '${name}' -> '${target}' is not present in the replica`);
            }
        }
        // Binding-time validation, name-resolvability only: every qualified
        // (group.table) FK / exists target named by the pinned schema must have
        // its group-name declared in `bindings`. Whether that bound group's
        // schema currently has the table/columns is NOT checked here — a missing
        // foreign table/column is a runtime data condition (an FK write against it
        // is voided at-use; an `exists` over it is false), not a create rejection.
        // (The schema itself cannot check this: it is shared across group instances
        // and knows no bindings.)
        const bindings = (_c = create.bindings) !== null && _c !== void 0 ? _c : {};
        for (const groupName of qualifiedTargetGroups(schemaView)) {
            if (!Object.prototype.hasOwnProperty.call(bindings, groupName)) {
                return validationFailure(`schema target group '${groupName}' is not bound`);
            }
        }
        // every canObserve gate must key a DECLARED binding name: the gate is
        // attributed to an observation through the binding's id, so an entry for a
        // non-bound name could never be reached (and signals a malformed create).
        for (const groupName of Object.keys((_d = create.canObserve) !== null && _d !== void 0 ? _d : {})) {
            if (!Object.prototype.hasOwnProperty.call(bindings, groupName)) {
                return validationFailure(`canObserve gate names group '${groupName}' which is not bound`);
            }
        }
        // idProvider selection: a LOCAL provider must exist in the pinned schema and
        // be flagged idProvider; a qualified 'group.table' provider is
        // name-resolvability only (its group must be bound — the foreign table being
        // a provider is checked at runtime, fail-closed).
        if (create.idProvider !== undefined) {
            if (!isValidTableRef(create.idProvider))
                return validationFailure(`idProvider '${create.idProvider}' is not a valid table reference`);
            const [groupName, table] = splitTableRef(create.idProvider);
            if (groupName === undefined) {
                if (!schemaView.hasTable(table))
                    return validationFailure(`idProvider table '${table}' does not exist`);
                if (schemaView.getIdProvider(table) === undefined)
                    return validationFailure(`table '${table}' is not an identity provider`);
            }
            else {
                if (!Object.prototype.hasOwnProperty.call(bindings, groupName)) {
                    return validationFailure(`idProvider group '${groupName}' is not bound`);
                }
            }
        }
        return validationOk();
    });
}
// The distinct group-names of all qualified (group.table) FK and exists
// targets in the effective schema.
function qualifiedTargetGroups(schemaView) {
    var _a, _b;
    const groups = new Set();
    for (const table of schemaView.getTableNames()) {
        for (const target of Object.values(schemaView.getFKs(table))) {
            const [group] = splitTableRef(target);
            if (group !== undefined)
                groups.add(group);
        }
        for (const restriction of (_b = (_a = schemaView.getTable(table)) === null || _a === void 0 ? void 0 : _a.restrictions) !== null && _b !== void 0 ? _b : []) {
            for (const atom of collectExistsAtoms(restriction.rule)) {
                const [group] = splitTableRef(atom.table);
                if (group !== undefined)
                    groups.add(group);
            }
        }
    }
    return groups;
}
// AUTHENTICATION at validation (the op's own (at, at) position). A bad or
// present-but-unresolvable signature is a HARD REJECT (false): the op is
// discarded, never a prev, never gossiped. A missing bound provider OBJECT
// throws out of resolveAuthorKey (the sync layer defers, then revalidates). An
// unauthored op is validly anonymous. A group with no idProvider performs no
// authentication — the claimed author is trusted (configure an idProvider to
// make authorship sound). Verdict is monotone, so the view-time `from` never
// refines it (computeEntryVoided then TRUSTS op.author).
function verifyOpAuthorship(op, group, at) {
    return __awaiter(this, void 0, void 0, function* () {
        const author = extractAuthor(op);
        if (author === undefined)
            return validationOk(); // anonymous: nothing to verify
        if (group.getIdProvider() === undefined)
            return validationOk(); // no authentication configured
        return (yield verifyPayloadSignature(op, (keyId) => group.resolveAuthorKey(keyId, at)))
            ? validationOk()
            : validationFailure(`signature from author '${author}' could not be verified`);
    });
}
// Authorization is a hard validation gate at the parent frontier. Bundle
// sibling writes do not authorize each other; grants must causally precede the
// op they authorize.
function validateRowOpRestrictionAt(op, table, schemaView, group, at) {
    return __awaiter(this, void 0, void 0, function* () {
        const rule = schemaView.getRestriction(table, op.action);
        const ok = yield evaluateRowOpRestriction(op, table, schemaView, (targetTable) => group.makeTable(targetTable).getView(at, at), (groupName, targetTable) => group.resolveForeignTableView(groupName, targetTable, at, at));
        return ok
            ? validationOk()
            : validationFailure(formatRestrictionFailureReason(table, op, rule));
    });
}
function validateRowEnvelope(envelope, group, at) {
    return __awaiter(this, void 0, void 0, function* () {
        const schemaView = yield group.resolveSchemaView(at);
        const op = envelope.op;
        const schemaResult = validateRowOpAgainstSchema(op, schemaView, envelope.table);
        if (!schemaResult.valid)
            return wrapValidationFailure(`row op for table '${envelope.table}' does not match schema`, schemaResult);
        // provider content-integrity (self-certifying identity rows)
        const providerResult = validateProviderInsertIntegrity(op, schemaView, envelope.table, group.getHashSuite());
        if (!providerResult.valid)
            return wrapValidationFailure(`provider row for table '${envelope.table}' is not self-certifying`, providerResult);
        // authentication (validation, at the op's own position)
        const authorshipResult = yield verifyOpAuthorship(op, group, at);
        if (!authorshipResult.valid)
            return authorshipResult;
        // identity / liveness at the op's own position
        const table = group.makeTable(envelope.table);
        if (op.action === 'insert') {
            // rowIds are write-once: any prior op for this rowId (insert OR
            // delete) at or below `at` makes the insert invalid — in particular
            // a deleted rowId can never be re-inserted
            const tableDag = yield table.getScopedDag();
            const cover = yield tableDag.findCoverWithFilter(at, { containsValues: { rows: [op.rowId] } });
            if (cover.size !== 0)
                return validationFailure(`rowId '${op.rowId}' already exists or was deleted in table '${envelope.table}'`);
        }
        else {
            // updates and deletes need an undeleted row at the op's own position
            // (base liveness: an FK-hidden but undeleted row can still be updated)
            if (!(yield table.baseHasRow(op.rowId, at)))
                return validationFailure(`rowId '${op.rowId}' is not live in table '${envelope.table}'`);
        }
        // Restrictions must pass at the parent frontier `(at, at)`; concurrent
        // revokes are still handled by view-time rechecks.
        const restrictionResult = yield validateRowOpRestrictionAt(op, envelope.table, schemaView, group, at);
        if (!restrictionResult.valid)
            return restrictionResult;
        // FK targets must be live at `at` (single-op entry: no bundle siblings).
        // A cross-group target resolves through the bound foreign group at the
        // foreign version observed at `at`; an absent foreign table is a missing
        // reference (not live), so the dangling write is rejected.
        return fkTargetsLive(op, envelope.table, schemaView, (ref, rowId) => __awaiter(this, void 0, void 0, function* () {
            const [groupName, targetTable] = splitTableRef(ref);
            if (groupName !== undefined) {
                const fv = yield group.resolveForeignTableView(groupName, targetTable, at, at);
                return fv !== undefined && fv.hasRow(rowId);
            }
            return (yield group.makeTable(targetTable).getView(at, at)).hasRow(rowId);
        }));
    });
}
// Every FK column the op carries (or inherits from a default) must name a
// live target row, per `isTargetLive` (the full target ref, local or
// `group.table`). Deletes carry no FK obligations.
function fkTargetsLive(op, table, schemaView, isTargetLive) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (op.action === 'delete')
            return validationOk();
        const fks = schemaView.getFKs(table);
        const def = schemaView.getTable(table);
        for (const column of Object.keys(fks)) {
            const value = (_a = op.values[column]) !== null && _a !== void 0 ? _a : (_b = def === null || def === void 0 ? void 0 : def.columns[column]) === null || _b === void 0 ? void 0 : _b.default;
            if (value === undefined)
                continue; // absent (nullable): unconstrained
            if (typeof value !== 'string')
                return validationFailure(`FK column '${column}' in table '${table}' must be a rowId string`);
            if (!(yield isTargetLive(fks[column], value))) {
                return validationFailure(`FK column '${column}' in table '${table}' points to non-live row '${value}' in '${fks[column]}'`);
            }
        }
        return validationOk();
    });
}
// A bundle is a single entry carrying an ordered list of ops across member
// tables. All-or-nothing: any invalid op rejects the whole bundle.
//
//   - every table exists; each op conforms to the schema;
//   - each rowId appears in at most one op across the bundle;
//   - identity / liveness and restrictions at the PRE-state (`at`): write-once
//     inserts, live rows for updates/deletes, authorizing rows already present;
//   - FK checks at each op's SEQUENTIAL CUT: a target is live iff it is live
//     at `at` and not deleted by an earlier op, OR inserted by an earlier op
//     (and not since deleted) — bundle order matters (decision 4).
function validateBundle(bundle, group, at) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const schemaView = yield group.resolveSchemaView(at);
        const seenRowIds = new Set();
        // effective insert/delete state contributed by earlier bundle ops, per table
        const insertedBefore = new Map();
        const deletedBefore = new Map();
        // Local targets use the sequential-cut overlay (earlier bundle ops);
        // cross-group targets resolve through the bound foreign group (no bundle
        // op can touch a foreign group, so no overlay applies there).
        const isTargetLive = (targetRef, rowId) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const [groupName, targetTable] = splitTableRef(targetRef);
            if (groupName !== undefined) {
                const fv = yield group.resolveForeignTableView(groupName, targetTable, at, at);
                return fv !== undefined && fv.hasRow(rowId);
            }
            if ((_b = ((_a = deletedBefore.get(targetTable)) === null || _a === void 0 ? void 0 : _a.has(rowId))) !== null && _b !== void 0 ? _b : false)
                return false;
            if ((_d = ((_c = insertedBefore.get(targetTable)) === null || _c === void 0 ? void 0 : _c.has(rowId))) !== null && _d !== void 0 ? _d : false)
                return true;
            return (yield group.makeTable(targetTable).getView(at, at)).hasRow(rowId);
        });
        for (const [index, write] of bundle.writes.entries()) {
            const table = write.table;
            const op = write.op;
            const schemaResult = validateRowOpAgainstSchema(op, schemaView, table);
            if (!schemaResult.valid) {
                return wrapValidationFailure(`bundle write ${index} for table '${table}' does not match schema`, schemaResult);
            }
            // provider content-integrity + authentication (each bundle op is signed)
            const providerResult = validateProviderInsertIntegrity(op, schemaView, table, group.getHashSuite());
            if (!providerResult.valid) {
                return wrapValidationFailure(`bundle write ${index} provider row for table '${table}' is not self-certifying`, providerResult);
            }
            const authorshipResult = yield verifyOpAuthorship(op, group, at);
            if (!authorshipResult.valid)
                return wrapValidationFailure(`bundle write ${index} has invalid authorship`, authorshipResult);
            if (seenRowIds.has(op.rowId)) {
                return validationFailure(`bundle writes rowId '${op.rowId}' more than once`);
            }
            seenRowIds.add(op.rowId);
            const tbl = group.makeTable(table);
            if (op.action === 'insert') {
                const tableDag = yield tbl.getScopedDag();
                const cover = yield tableDag.findCoverWithFilter(at, { containsValues: { rows: [op.rowId] } });
                if (cover.size !== 0)
                    return validationFailure(`bundle insert rowId '${op.rowId}' already exists or was deleted in table '${table}'`);
            }
            else {
                if (!(yield tbl.baseHasRow(op.rowId, at)))
                    return validationFailure(`bundle op rowId '${op.rowId}' is not live in table '${table}'`);
            }
            // Authorization reads only the parent frontier; sibling writes in this
            // bundle can satisfy FK targets, but not permission predicates.
            const restrictionResult = yield validateRowOpRestrictionAt(op, table, schemaView, group, at);
            if (!restrictionResult.valid)
                return wrapValidationFailure(`bundle write ${index} failed restrictions`, restrictionResult);
            const fkResult = yield fkTargetsLive(op, table, schemaView, isTargetLive);
            if (!fkResult.valid)
                return wrapValidationFailure(`bundle write ${index} failed FK checks`, fkResult);
            if (op.action === 'insert') {
                ((_a = insertedBefore.get(table)) !== null && _a !== void 0 ? _a : setInMap(insertedBefore, table)).add(op.rowId);
            }
            else if (op.action === 'delete') {
                ((_b = deletedBefore.get(table)) !== null && _b !== void 0 ? _b : setInMap(deletedBefore, table)).add(op.rowId);
            }
        }
        return validationOk();
    });
}
function setInMap(map, key) {
    const set = new Set();
    map.set(key, set);
    return set;
}
// A ref-advance is either THE schema deploy (the group's own schema ref,
// canDeploy-gated) or a foreign-group observation (a bound group's id, no
// deploy gate). Both are barriers. Any other refId is unknown.
function validateRefAdvance(payload, group, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (payload.refId === group.getSchemaRef()) {
            return validateDeploy(payload, group, at);
        }
        // foreign-group observation (cross-group FK / exists). The refId must be a
        // bound group; advancing observation needs no deploy authority unless the
        // binding declares a canObserve gate. A missing bound-group object is an
        // infrastructure error (throw, in getForeignGroupCausalDag), never a `false`.
        const boundIds = new Set(Object.values(group.getBindings()));
        if (!boundIds.has(payload.refId))
            return validationFailure(`ref '${payload.refId}' is neither the schema nor a bound foreign group`);
        const foreignCausalDag = yield group.getForeignGroupCausalDag(payload.refId);
        const newRefVersion = extractRefVersion(payload);
        const groupDag = yield group.getScopedDag();
        if (!(yield validateRefAdvanceMonotonicity(groupDag, foreignCausalDag, payload.refId, newRefVersion, at))) {
            return validationFailure(`ref-advance for bound group '${payload.refId}' is not monotonic`);
        }
        return validateObserveGate(payload, group, at, newRefVersion);
    });
}
// canObserve gate at write-admission (mirrors validateDeploy's canDeploy block):
// when the observed binding declares a gate, the observation must be authored,
// its signature must verify (when a provider is configured), and the gate must
// hold in the OBSERVED group's frame AT THE IMPORTED VERSION (refAt = refFrom =
// newRefVersion): "was the author authorized in G at the version they import".
// At-use voiding (computeEntryVoided) then catches a back-dated observation a
// later concurrent revoke retroactively unauthorizes.
function validateObserveGate(payload, group, at, newRefVersion) {
    return __awaiter(this, void 0, void 0, function* () {
        if (group.observeGateFor(payload.refId) === undefined)
            return validationOk(); // ungated binding
        const p = payload;
        const author = extractAuthor(p);
        if (author === undefined) {
            return validationFailure(`observation of gated group '${payload.refId}' must be authored`);
        }
        // AUTHENTICATION at the op's own position (the group's OWN provider), like
        // a deploy: a present-but-unverifiable signature is a hard reject.
        if (group.getIdProvider() !== undefined) {
            if (!(yield verifyPayloadSignature(p, (keyId) => group.resolveAuthorKey(keyId, at)))) {
                return validationFailure(`observation signature from author '${author}' could not be verified`);
            }
        }
        return (yield group.evaluateObserveGate(payload.refId, author, newRefVersion, newRefVersion))
            ? validationOk()
            : validationFailure(`canObserve predicate rejected observation of '${bindingNameFor(group, payload.refId)}': ${formatPredicate(group.observeGateFor(payload.refId))}`);
    });
}
function bindingNameFor(group, refId) {
    for (const [name, id] of Object.entries(group.getBindings())) {
        if (id === refId)
            return name;
    }
    return refId;
}
function validateDeploy(payload, group, at) {
    return __awaiter(this, void 0, void 0, function* () {
        const p = payload;
        const author = extractAuthor(p);
        // AUTHENTICATION at validation: when the deploy is authored and the group
        // configures a provider (its OWN provider — the deploying group's scope),
        // the signature must verify against the resolved key, else HARD REJECT. A
        // missing bound provider object throws (defer). canDeploy then evaluates
        // $author against the VERIFIED author.
        if (author !== undefined && group.getIdProvider() !== undefined) {
            if (!(yield verifyPayloadSignature(p, (keyId) => group.resolveAuthorKey(keyId, at)))) {
                return validationFailure(`deploy signature from author '${author}' could not be verified`);
            }
        }
        // canDeploy is evaluated against the (now verified) ref-advance author
        // ('object' context: $author only, no subject row). A missing author makes
        // $author-dependent terms unprovable.
        const canDeploy = group.getCanDeploy();
        if (canDeploy !== undefined) {
            const ok = yield evaluatePredicate(canDeploy, {
                getTableView: (table) => __awaiter(this, void 0, void 0, function* () { return group.makeTable(table).getView(at, at); }),
                getForeignTableView: (groupName, table) => group.resolveForeignTableView(groupName, table, at, at),
                author,
                context: 'object',
            });
            if (!ok)
                return validationFailure(`canDeploy predicate rejected schema deploy: ${formatPredicate(canDeploy)}`);
        }
        const newRefVersion = extractRefVersion(payload);
        const schema = yield group.getSchemaObject();
        const schemaCausalDag = yield schema.getCausalDag();
        // never deploy below the pinned genesis version
        if (!(yield refVersionAtOrAbove(schemaCausalDag, newRefVersion, group.getPinnedSchemaVersion()))) {
            return validationFailure("schema deploy is below the pinned schema version");
        }
        const groupDag = yield group.getScopedDag();
        if (!(yield validateRefAdvanceMonotonicity(groupDag, schemaCausalDag, payload.refId, newRefVersion, at))) {
            return validationFailure("schema deploy ref-advance is not monotonic");
        }
        return validateAddFkPrerequisite(group, at, newRefVersion);
    });
}
// add-fk PREREQUISITE (one-time, hard reject): a deploy that newly enforces an
// FK must not strand existing data. For each FK added or retargeted by this
// deploy (vs the currently-deployed schema), every row live at `at` whose
// (pre-existing) FK column carries a value must reach a live target at `at`.
// Rationale: FK reach is at-use, so once deployed the FK is inert for these old
// rows (a causally-later target delete never voids them) — this is the single
// point-in-time consistency check that the data honored the FK when adopted.
// New columns hold no old explicit values (only the uniform schema default, a
// schema-level effect), so they are not enumerated here.
function validateAddFkPrerequisite(group, at, newRefVersion) {
    return __awaiter(this, void 0, void 0, function* () {
        const oldSchema = yield group.resolveSchemaView(at);
        const schema = yield group.getSchemaObject();
        const newVersion = version(...group.getPinnedSchemaVersion());
        for (const hash of newRefVersion)
            newVersion.add(hash);
        const newSchema = yield schema.getView(newVersion, newVersion);
        const isTargetLive = (targetRef, rowId) => __awaiter(this, void 0, void 0, function* () {
            const [groupName, targetTable] = splitTableRef(targetRef);
            if (groupName !== undefined) {
                const fv = yield group.resolveForeignTableView(groupName, targetTable, at, at);
                return fv !== undefined && fv.hasRow(rowId);
            }
            return (yield group.makeTable(targetTable).getView(at, at)).hasRow(rowId);
        });
        for (const table of newSchema.getTableNames()) {
            const newFks = newSchema.getFKs(table);
            const oldFks = oldSchema.hasTable(table) ? oldSchema.getFKs(table) : {};
            const addedColumns = Object.keys(newFks).filter((c) => oldFks[c] !== newFks[c]);
            if (addedColumns.length === 0)
                continue;
            const view = yield group.makeTable(table).getView(at, at);
            for (const rowId of yield view.liveRowIds()) {
                const row = yield view.getRow(rowId);
                if (row === undefined)
                    continue;
                for (const column of addedColumns) {
                    const value = row.values[column];
                    if (value === undefined)
                        continue; // nullable / absent: unconstrained
                    if (typeof value !== 'string') {
                        return validationFailure(`existing FK column '${column}' in table '${table}' is not a rowId string`);
                    }
                    if (!(yield isTargetLive(newFks[column], value))) {
                        return validationFailure(`deploy would strand row '${rowId}' in table '${table}' on FK '${column}' -> '${newFks[column]}'`);
                    }
                }
            }
        }
        return validationOk();
    });
}
